import React, { useState, useEffect, useRef, useCallback } from 'react'
import { createPortal, flushSync } from 'react-dom'
import Modal from '../../../Modal';
import styles from './index.module.scss'
import ColorPicker from "../../../Pickers/ColorPicker";
import DropDown from "../../../DropDown";
import ColorItemPicker from "../../../Pickers/ColorPicker/components/ColorItemPicker";
import InputName from "./components/InputName";
import Radio from "../../../Radio";
import { useWorkspace } from "../../../../context/workspace.context";
import WorkspaceMembersService from "../../../../services/workspace-members.service";
import ProjectService from "../../../../services/project.service";
import CheckboxActiveIcon from "../../../../assets/icon/CheckboxActiveIcon";
import CheckboxIcon from "../../../../assets/icon/CheckboxIcon";
import DotsVerticalIcon from "../../../../assets/icon/DotsVerticalIcon";
import PaletteIcon from "../../../../assets/icon/PaletteIcon";
import { FIXED_PROJECT_STATUS_COLORS, PROJECT_STATUS_COLORS } from "../../../../utils/projectStatus";

interface Member {
    id: string
    email: string
    first_name: string
    last_name: string
    avatar?: string
}

interface StatusPreset {
    key: string
    title: string
    statuses: {
        key: string
        name: string
        color: string
        position: number
    }[]
}

interface StatusItem {
    id: string
    name: string
    color: string
    position: number
    isFixed: boolean
}

interface CreateProjectModalProps {
    onClose: () => void
    createProject: (projectData: any) => void
}

interface ValidationErrors {
    name?: string
    statusPreset?: string
}

const REQUIRED_FIELD_ERROR = 'Обязательное поле'
const REQUIRED_FIELDS_MESSAGE = 'Заполните все обязательные поля'

const STATUS_COLORS = PROJECT_STATUS_COLORS
const FIXED_STATUS_COLOR_BY_KEY = FIXED_PROJECT_STATUS_COLORS

const MAX_STATUSES = 10

let statusIdCounter = 0
const generateStatusId = () => `status_${Date.now()}_${++statusIdCounter}`

const getRandomItem = <T,>(items: T[]) => items[Math.floor(Math.random() * items.length)]

const getRandomAvailableColor = (statuses: StatusItem[], excludedStatusIds: string[] = [], blockedColors: string[] = []) => {
    const usedColors = new Set(
        statuses
            .filter((status) => !excludedStatusIds.includes(status.id))
            .map((status) => status.color)
    )
    const blockedColorSet = new Set(blockedColors)
    const availableColors = STATUS_COLORS.filter((color) => !usedColors.has(color) && !blockedColorSet.has(color))

    return getRandomItem(availableColors) || getRandomItem(STATUS_COLORS)
}

const assignUniqueStatusColors = (statuses: StatusItem[]) => {
    const usedColors = new Set<string>()

    return statuses.map((status) => {
        const fixedColor = FIXED_STATUS_COLOR_BY_KEY[status.id]
        const preferredColor = fixedColor || status.color
        const nextColor = preferredColor && STATUS_COLORS.includes(preferredColor) && !usedColors.has(preferredColor)
            ? preferredColor
            : getRandomItem(STATUS_COLORS.filter((color) => !usedColors.has(color))) || preferredColor || STATUS_COLORS[0]

        usedColors.add(nextColor)

        return {
            ...status,
            color: nextColor,
        }
    })
}

export default function ProjectModal({ onClose, createProject }: CreateProjectModalProps) {
    const { workspace } = useWorkspace()
    const [step, setStep] = useState(1)
    const [taskData, setTaskData] = useState<any>({
        initial_members: [],
        color: "#FF5B5D",
        name: "",
        is_public: true,
        status_preset_key: "",
    });

    const [members, setMembers] = useState<Member[]>([])
    const [membersOpen, setMembersOpen] = useState(false)
    const [isColorPickerOpen, setIsColorPickerOpen] = useState(false)
    const [search, setSearch] = useState('')
    const [validationErrors, setValidationErrors] = useState<ValidationErrors>({})
    const [validationMessage, setValidationMessage] = useState<string | null>(null)
    const membersRef = useRef<HTMLDivElement>(null)

    const [presets, setPresets] = useState<StatusPreset[]>([])
    const [presetsOpen, setPresetsOpen] = useState(false)
    const presetsRef = useRef<HTMLDivElement>(null)

    const [statuses, setStatuses] = useState<StatusItem[]>([])
    const [openMenuId, setOpenMenuId] = useState<string | null>(null)
    const [editingId, setEditingId] = useState<string | null>(null)
    const [editingName, setEditingName] = useState('')
    const [colorPickerId, setColorPickerId] = useState<string | null>(null)
    const [dragItemId, setDragItemId] = useState<string | null>(null)
    const [dragOverId, setDragOverId] = useState<string | null>(null)

    const editInputRef = useRef<HTMLInputElement>(null)
    const statusRowRefs = useRef<Map<string, HTMLDivElement>>(new Map())
    const colorPickerPortalRef = useRef<HTMLDivElement>(null)
    const [colorPickerPortalPos, setColorPickerPortalPos] = useState<{ top: number; left: number } | null>(null)

    useEffect(() => {
        if (!workspace) return
        const fetchMembers = async () => {
            try {
                const [data, photosData] = await Promise.all([
                    WorkspaceMembersService.getMembers(workspace.id),
                    WorkspaceMembersService.getMemberPhotos(workspace.id).catch(() => null),
                ])
                const raw = data.members || data || []
                const photoItems: any[] = photosData?.items || []
                const photoMap = new Map<string, string>()

                photoItems.forEach((photo: any) => {
                    if (photo.user_id && photo.photo_data_url) {
                        photoMap.set(String(photo.user_id), photo.photo_data_url)
                    }
                })

                const list: Member[] = raw.map((m: any) => {
                    const user = m.user || {}
                    const userId = m.user_id ?? user.user_id ?? user.id ?? m.id
                    return {
                        id: userId,
                        email: user.email ?? m.email ?? '',
                        first_name: user.first_name ?? m.first_name ?? '',
                        last_name: user.last_name ?? m.last_name ?? '',
                        avatar: photoMap.get(String(userId))
                            ?? user.avatar
                            ?? user.photo
                            ?? user.photo_data_url
                            ?? user.avatar_url
                            ?? m.avatar
                            ?? m.photo
                            ?? m.photo_data_url
                            ?? m.avatar_url
                            ?? undefined,
                    }
                })
                setMembers(list)
            } catch (e) {
                console.error('Failed to fetch workspace members:', e)
            }
        }
        fetchMembers()
    }, [workspace])

    useEffect(() => {
        const fetchPresets = async () => {
            try {
                const data = await ProjectService.getStatusPresets()
                setPresets(data.presets || [])
            } catch (e) {
                console.error('Failed to fetch status presets:', e)
            }
        }
        fetchPresets()
    }, [])

    useEffect(() => {
        const handleClickOutside = (e: MouseEvent) => {
            if (membersRef.current && !membersRef.current.contains(e.target as Node)) {
                setMembersOpen(false)
            }
            if (presetsRef.current && !presetsRef.current.contains(e.target as Node)) {
                setPresetsOpen(false)
            }
        }
        document.addEventListener('mousedown', handleClickOutside)
        return () => document.removeEventListener('mousedown', handleClickOutside)
    }, [])

    useEffect(() => {
        if (!editingId) return

        const frameId = window.requestAnimationFrame(() => {
            const input = editInputRef.current
            if (!input) return

            input.focus()
            const cursorPosition = input.value.length
            input.setSelectionRange(cursorPosition, cursorPosition)
        })

        return () => window.cancelAnimationFrame(frameId)
    }, [editingId])

    useEffect(() => {
        if (!colorPickerId) {
            setColorPickerPortalPos(null)
            return
        }
        const el = statusRowRefs.current.get(colorPickerId)
        if (!el) { setColorPickerPortalPos(null); return }
        const rect = el.getBoundingClientRect()
        setColorPickerPortalPos({ top: rect.bottom + 8, left: rect.left })
    }, [colorPickerId])

    useEffect(() => {
        if (!colorPickerId) return
        const handle = (e: MouseEvent) => {
            if (colorPickerPortalRef.current && !colorPickerPortalRef.current.contains(e.target as Node)) {
                setColorPickerId(null)
            }
        }
        document.addEventListener('mousedown', handle)
        return () => document.removeEventListener('mousedown', handle)
    }, [colorPickerId])

    useEffect(() => {
        if (!colorPickerId) return
        const handleScroll = () => setColorPickerId(null)
        window.addEventListener('scroll', handleScroll, true)
        return () => window.removeEventListener('scroll', handleScroll, true)
    }, [colorPickerId])

    const getMemberName = (member: Member) => {
        if (member.first_name || member.last_name) {
            return `${member.first_name || ''} ${member.last_name || ''}`.trim()
        }
        return member.email
    }

    const getMemberInitials = (member: Member) => {
        const initials = `${member.first_name?.[0] || ''}${member.last_name?.[0] || ''}`.toUpperCase()

        return initials || member.email?.[0]?.toUpperCase() || 'U'
    }

    const toggleMember = (memberId: string) => {
        const current = taskData.initial_members
        const newMembers = current.includes(memberId)
            ? current.filter((id: string) => id !== memberId)
            : [...current, memberId]

        setTaskData({ ...taskData, initial_members: newMembers })
    }

    const filteredMembers = members.filter(m => {
        if (!search) return true
        const name = getMemberName(m).toLowerCase()
        return name.includes(search.toLowerCase()) || m.email?.toLowerCase().includes(search.toLowerCase())
    })

    const selectedMembers = members.filter(m => taskData.initial_members.includes(m.id))
    const selectedPreset = presets.find(p => p.key === taskData.status_preset_key)
    const isPrivate = !taskData.is_public
    const isNameValid = Boolean(taskData.name.trim())
    const isPresetSelected = Boolean(taskData.status_preset_key)

    const setColor = (color: string) => setTaskData({ ...taskData, color })
    const setTitle = (name: string) => {
        setTaskData({ ...taskData, name })
        if (name.trim()) {
            setValidationErrors((current) => ({ ...current, name: undefined }))
            setValidationMessage(null)
        }
    }
    const setIsPrivate = (nextIsPrivate: boolean) => setTaskData({ ...taskData, is_public: !nextIsPrivate })

    useEffect(() => {
        if (!isPrivate) {
            setMembersOpen(false)
            setSearch('')
        }
    }, [isPrivate])

    const selectPreset = (presetKey: string) => {
        setTaskData({ ...taskData, status_preset_key: presetKey })
        setValidationErrors((current) => ({ ...current, statusPreset: undefined }))
        setValidationMessage(null)
        setPresetsOpen(false)

        const preset = presets.find(p => p.key === presetKey)
        if (preset && preset.statuses.length > 0) {
            const sorted = [...preset.statuses].sort((a, b) => a.position - b.position)
            const FIXED_KEYS = ['backlog', 'in_progress', 'complete']
            const mapped: StatusItem[] = sorted.map((s, i) => ({
                id: s.key || generateStatusId(),
                name: s.name,
                color: FIXED_STATUS_COLOR_BY_KEY[s.key] || s.color,
                position: i,
                isFixed: i === 0 || i === sorted.length - 1 || FIXED_KEYS.includes(s.key),
            }))
            setStatuses(assignUniqueStatusColors(mapped))
        }
    }

    const REOPENED_KEY = 'reopened'

    const addStatus = () => {
        if (statuses.length >= MAX_STATUSES) return
        const newStatus: StatusItem = {
            id: generateStatusId(),
            name: '',
            color: getRandomAvailableColor(statuses),
            position: statuses.length - 1,
            isFixed: false,
        }
        const reopenedIndex = statuses.findIndex(s => s.id === REOPENED_KEY)
        const completedIndex = statuses.length - (reopenedIndex !== -1 ? 2 : 1)
        const before = statuses.slice(0, completedIndex)
        const after = statuses.slice(completedIndex)
        setStatuses([...before, newStatus, ...after])

        setEditingId(newStatus.id)
        setEditingName('')
        setOpenMenuId(null)
    }

    const closeStatusActionMenu = () => {
        flushSync(() => {
            setOpenMenuId(null)
        })
    }

    const removeReopened = () => {
        setStatuses(prev => prev.filter(s => s.id !== REOPENED_KEY))
        closeStatusActionMenu()
    }

    const deleteStatus = (id: string) => {
        setStatuses(prev => prev.filter(s => s.id !== id))
        closeStatusActionMenu()
        setColorPickerId(null)
    }

    const startRename = (status: StatusItem) => {
        closeStatusActionMenu()
        setEditingId(status.id)
        setEditingName(status.name)
        setColorPickerId(null)
    }

    const finishRename = () => {
        if (editingId && editingName.trim()) {
            setStatuses(prev => prev.map(s =>
                s.id === editingId ? { ...s, name: editingName.trim() } : s
            ))
        }
        setEditingId(null)
        setEditingName('')
    }

    const changeStatusColor = (id: string, color: string) => {
        setStatuses(prev => {
            const target = prev.find(s => s.id === id)
            if (!target || target.isFixed) return prev

            const conflict = prev.find(s => s.id !== id && s.color === color)
            if (conflict?.isFixed) return prev

            return prev.map(s => {
                if (s.id === id) {
                    return { ...s, color }
                }

                if (conflict && s.id === conflict.id && !s.isFixed) {
                    return {
                        ...s,
                        color: getRandomAvailableColor(prev, [id, s.id], [color]),
                    }
                }

                return s
            })
        })
        setColorPickerId(null)
        setOpenMenuId(null)
    }

    const openStatusColorPicker = (id: string) => {
        closeStatusActionMenu()
        setColorPickerId(id)
    }

    // Drag and drop — Backlog, Complete, and Reopened can't be dragged
    const canDrag = (status: StatusItem, index: number) => {
        if (index === 0) return false
        if (status.isFixed) return false
        if (status.id === REOPENED_KEY) return false
        return true
    }

    // Menu (rename/color/delete) — only for non-fixed statuses; Reopened gets only delete
    const hasMenu = (status: StatusItem) => {
        if (status.id === REOPENED_KEY) return true
        return !status.isFixed
    }

    const handleDragStart = (e: React.DragEvent, id: string) => {
        setDragItemId(id)
        e.dataTransfer.effectAllowed = 'move'
    }

    const isDropDisabled = useCallback((status: StatusItem, index: number) => {
        return index === 0 || status.isFixed || status.id === REOPENED_KEY
    }, [])

    const handleDragOver = useCallback((e: React.DragEvent, id: string, index: number) => {
        e.preventDefault()
        const target = statuses[index]
        if (isDropDisabled(target, index)) return
        if (id !== dragItemId) {
            setDragOverId(id)
        }
    }, [dragItemId, statuses, isDropDisabled])

    const handleDragLeave = () => {
        setDragOverId(null)
    }

    const handleDrop = useCallback((e: React.DragEvent, targetId: string, targetIndex: number) => {
        e.preventDefault()
        setDragOverId(null)

        if (!dragItemId || dragItemId === targetId) {
            setDragItemId(null)
            return
        }
        const target = statuses[targetIndex]
        if (!target || isDropDisabled(target, targetIndex)) {
            setDragItemId(null)
            return
        }

        setStatuses(prev => {
            const dragIndex = prev.findIndex(s => s.id === dragItemId)
            const dropIndex = prev.findIndex(s => s.id === targetId)
            if (dragIndex === -1 || dropIndex === -1) return prev

            const newList = [...prev]
            const [dragged] = newList.splice(dragIndex, 1)
            newList.splice(dropIndex, 0, dragged)
            return newList
        })

        setDragItemId(null)
    }, [dragItemId, statuses, isDropDisabled])

    const handleDragEnd = () => {
        setDragItemId(null)
        setDragOverId(null)
    }

    const showRequiredFieldsMessage = (errors: ValidationErrors) => {
        setValidationErrors(errors)
        setValidationMessage(REQUIRED_FIELDS_MESSAGE)
    }

    const continueToStatuses = () => {
        if (!isNameValid) {
            showRequiredFieldsMessage({ name: REQUIRED_FIELD_ERROR })
            return
        }

        setValidationErrors({})
        setValidationMessage(null)
        setStep(2)
    }

    const returnToProjectDetails = () => {
        setStep(1)
        setValidationErrors((current) => ({ ...current, statusPreset: undefined }))
        setValidationMessage(null)
    }

    const createProjectHandler = async () => {
        const errors: ValidationErrors = {}
        if (!isNameValid) errors.name = REQUIRED_FIELD_ERROR
        if (!isPresetSelected) errors.statusPreset = REQUIRED_FIELD_ERROR

        if (Object.keys(errors).length > 0) {
            showRequiredFieldsMessage(errors)
            if (errors.name) setStep(1)
            return
        }

        const task_statuses = statuses.map((s, i) => ({
            key: s.id,
            name: s.name,
            color: s.color,
            position: i,
        }))
        const initial_members = (isPrivate ? taskData.initial_members : []).map((userId: string | number) => ({
            user_id: String(userId),
            role_in_project: "MEMBER",
        }))
        await createProject({
            name: taskData.name.trim(),
            color: taskData.color,
            is_public: taskData.is_public,
            status_preset: taskData.status_preset_key,
            initial_members,
            task_statuses,
        })
    }

    const DragHandleIcon = () => (
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <rect width="24" height="24" rx="12" fill="white"/>
            <path d="M10 6.00005C10 6.8285 9.32848 7.5001 8.50012 7.5001C7.67176 7.5001 7.00024 6.8285 7.00024 6.00005C7.00024 5.17159 7.67176 4.5 8.50012 4.5C9.32848 4.5 10 5.17159 10 6.00005Z" fill="currentColor"/>
            <path d="M9.99976 12.0012C9.99976 12.8297 9.32824 13.5013 8.49988 13.5013C7.67152 13.5013 7 12.8297 7 12.0012C7 11.1728 7.67152 10.5012 8.49988 10.5012C9.32824 10.5012 9.99976 11.1728 9.99976 12.0012Z" fill="currentColor"/>
            <path d="M9.99976 18C9.99976 18.8284 9.32824 19.5 8.49988 19.5C7.67152 19.5 7 18.8284 7 18C7 17.1715 7.67152 16.4999 8.49988 16.4999C9.32824 16.4999 9.99976 17.1715 9.99976 18Z" fill="currentColor"/>
            <path d="M17 6.00005C17 6.8285 16.3285 7.5001 15.5001 7.5001C14.6718 7.5001 14.0002 6.8285 14.0002 6.00005C14.0002 5.17159 14.6718 4.5 15.5001 4.5C16.3285 4.5 17 5.17159 17 6.00005Z" fill="currentColor"/>
            <path d="M16.9998 12.0012C16.9998 12.8297 16.3282 13.5013 15.4999 13.5013C14.6715 13.5013 14 12.8297 14 12.0012C14 11.1728 14.6715 10.5012 15.4999 10.5012C16.3282 10.5012 16.9998 11.1728 16.9998 12.0012Z" fill="currentColor"/>
            <path d="M16.9998 18C16.9998 18.8284 16.3282 19.5 15.4999 19.5C14.6715 19.5 14 18.8284 14 18C14 17.1715 14.6715 16.4999 15.4999 16.4999C16.3282 16.4999 16.9998 17.1715 16.9998 18Z" fill="currentColor"/>
        </svg>
    )

    const LockIcon = () => (
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M11.9 4C12.9609 4 13.9783 4.42143 14.7284 5.17157C15.4786 5.92172 15.9 6.93913 15.9 8V10.4C16.5365 10.4 17.147 10.6529 17.5971 11.1029C18.0471 11.553 18.3 12.1635 18.3 12.8V17.6C18.3 18.2365 18.0471 18.847 17.5971 19.2971C17.147 19.7471 16.5365 20 15.9 20H7.9C7.26348 20 6.65303 19.7471 6.20294 19.2971C5.75286 18.847 5.5 18.2365 5.5 17.6V12.8C5.5 12.1635 5.75286 11.553 6.20294 11.1029C6.65303 10.6529 7.26348 10.4 7.9 10.4V8C7.9 6.93913 8.32143 5.92172 9.07157 5.17157C9.82172 4.42143 10.8391 4 11.9 4ZM11.9 13.6C11.4963 13.5999 11.1075 13.7523 10.8116 14.0268C10.5156 14.3013 10.3343 14.6775 10.304 15.08L10.3 15.2C10.3 15.5164 10.3938 15.8258 10.5696 16.0889C10.7455 16.352 10.9953 16.5571 11.2877 16.6782C11.5801 16.7993 11.9018 16.831 12.2121 16.7693C12.5225 16.7075 12.8076 16.5551 13.0314 16.3314C13.2551 16.1076 13.4075 15.8225 13.4693 15.5121C13.531 15.2018 13.4993 14.8801 13.3782 14.5877C13.2571 14.2953 13.052 14.0455 12.7889 13.8696C12.5258 13.6938 12.2165 13.6 11.9 13.6ZM11.9 5.6C11.2635 5.6 10.653 5.85286 10.2029 6.30294C9.75286 6.75303 9.5 7.36348 9.5 8V10.4H14.3V8C14.3 7.36348 14.0471 6.75303 13.5971 6.30294C13.147 5.85286 12.5365 5.6 11.9 5.6Z" fill="currentColor"/>
        </svg>
    )

    const RenameStatusIcon = () => (
        <svg width="18" height="16" viewBox="0 0 18 16" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M14.3772 0.729439L15.2713 1.62358C15.7495 2.10175 15.7495 2.93527 15.2713 3.41272L13.9693 4.71479L11.3008 2.04631L12.6028 0.744237C12.8419 0.505174 13.158 0.373047 13.497 0.373047C13.8345 0.373047 14.1386 0.505174 14.3772 0.729439Z" fill="currentColor"/>
            <path d="M10.4023 2.93018L3.92464 9.40781C3.88318 9.44927 3.85397 9.50106 3.8408 9.55755L3.15599 12.4607C3.13151 12.5662 3.16256 12.6772 3.23975 12.7544C3.29908 12.8137 3.37911 12.8466 3.46196 12.8466C3.48542 12.8466 3.5098 12.8438 3.53334 12.8381L6.43653 12.1533C6.49299 12.1401 6.54569 12.1109 6.58617 12.0694L13.0638 5.5918L10.4023 2.93018Z" fill="currentColor"/>
            <path d="M15.8402 15.4007H7.67056C7.32367 15.4007 7.04199 15.119 7.04199 14.7721C7.04199 14.4252 7.32367 14.1436 7.67056 14.1436H15.8402C16.1871 14.1436 16.4688 14.4252 16.4688 14.7721C16.4688 15.1201 16.1871 15.4007 15.8402 15.4007Z" fill="currentColor"/>
        </svg>
    )

    const TrashStatusIcon = () => (
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M6.2 8.8C6.2 8.68954 6.28954 8.6 6.4 8.6H17.7111C17.8216 8.6 17.9111 8.68954 17.9111 8.8V19.65C17.9111 19.8754 17.8216 20.0916 17.6622 20.251C17.5027 20.4104 17.2865 20.5 17.0611 20.5H7.05C6.82457 20.5 6.60837 20.4104 6.44896 20.251C6.28955 20.0916 6.2 19.8754 6.2 19.65V8.8ZM8.75 6.05V4.35C8.75 4.12457 8.83955 3.90837 8.99896 3.74896C9.15837 3.58955 9.37457 3.5 9.6 3.5H14.5111C14.7365 3.5 14.9527 3.58955 15.1122 3.74896C15.2716 3.90837 15.3611 4.12457 15.3611 4.35V6.05H18.7611C19.2306 6.05 19.6111 6.43056 19.6111 6.9C19.6111 7.36944 19.2306 7.75 18.7611 7.75H5.35C4.88056 7.75 4.5 7.36944 4.5 6.9C4.5 6.43056 4.88056 6.05 5.35 6.05H8.75ZM10.65 5.2C10.5395 5.2 10.45 5.28954 10.45 5.4V5.85C10.45 5.96046 10.5395 6.05 10.65 6.05H13.4611C13.5716 6.05 13.6611 5.96046 13.6611 5.85V5.4C13.6611 5.28954 13.5716 5.2 13.4611 5.2H10.65ZM10.3556 12C9.88611 12 9.50556 12.3806 9.50556 12.85V16.25C9.50556 16.7194 9.88611 17.1 10.3556 17.1C10.825 17.1 11.2056 16.7194 11.2056 16.25V12.85C11.2056 12.3806 10.825 12 10.3556 12ZM13.7556 12C13.2861 12 12.9056 12.3806 12.9056 12.85V16.25C12.9056 16.7194 13.2861 17.1 13.7556 17.1C14.225 17.1 14.6056 16.7194 14.6056 16.25V12.85C14.6056 12.3806 14.225 12 13.7556 12Z" fill="#F04438"/>
        </svg>
    )

    const lastFixedNonReopenedIndex = statuses.reduce((acc, s, i) =>
        (s.isFixed && s.id !== REOPENED_KEY) ? i : acc, -1)

    return (
        <>
        <Modal
            isOpen={true}
            onRequestClose={onClose}
            width={446}
        >
            <div className={styles.createProjectModal}>
                <div className={styles.createProjectModal__header}>
                    {step === 2 && (
                        <div className={styles.createProjectModal__back} onClick={returnToProjectDetails}>
                            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                <path d="M15 18L9 12L15 6" stroke="#64617E" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                            </svg>
                        </div>
                    )}
                    <div className={styles.createProjectModal__header_title}>
                        {step === 1 ? 'Новый проект' : 'Настройка статусов внутри проекта'}
                    </div>
                    <div className={styles.createProjectModal__close} onClick={onClose}>
                        <img src="/icon/closeCircle.svg" alt="close" />
                    </div>
                </div>

                {validationMessage && (
                    <div className={styles.createProjectModal__validationBanner} role="alert">
                        <svg width="20" height="20" viewBox="0 0 20 20" fill="none" aria-hidden="true">
                            <circle cx="10" cy="10" r="8" stroke="currentColor" strokeWidth="1.5" />
                            <path d="M10 5.75V10.25" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
                            <circle cx="10" cy="13.5" r="0.9" fill="currentColor" />
                        </svg>
                        <span>{validationMessage}</span>
                    </div>
                )}

                {step === 1 && (
                    <div style={{ display: "flex" }}>
                        <div style={{ width: "100%" }}>
                            <div className={styles.createProjectModal__top}>
                                <div className={`${styles.createProjectModal__plate} ${styles.createProjectModal__iconPlate}`}>
                                    <div className={styles.createProjectModal__plate_title}>
                                        Значок
                                    </div>
                                    <ColorPicker
                                        getColor={setColor}
                                        currentColor={taskData.color}
                                        isOpen={isColorPickerOpen}
                                        setIsOpen={setIsColorPickerOpen}
                                    />
                                </div>
                                <div className={`${styles.createProjectModal__plate} ${styles.createProjectModal__namePlate}`}>
                                    <div className={styles.createProjectModal__plate_title}>
                                        Название<span className={styles.createProjectModal__required}>*</span>
                                    </div>
                                    <InputName
                                        getTitle={setTitle}
                                        value={taskData.name}
                                        hasError={Boolean(validationErrors.name)}
                                        describedBy="project-name-error"
                                    />
                                    {validationErrors.name && (
                                        <div id="project-name-error" className={styles.createProjectModal__fieldError} role="alert">
                                            {validationErrors.name}
                                        </div>
                                    )}
                                </div>
                            </div>
                            <div className={styles.createProjectModal__private}>
                                <div className={styles.createProjectModal__private_top}>
                                    <div className={styles.createProjectModal__sub}>
                                        Сделать приватно
                                    </div>
                                    <div>
                                        <Radio changeState={setIsPrivate} isOn={isPrivate} />
                                    </div>
                                </div>
                                <div className={styles.createProjectModal__private_description}>
                                    {isPrivate
                                        ? 'Только вы и приглашенные участники будут иметь доступ'
                                        : 'Проект будет виден всем в workspace'}
                                </div>
                            </div>
                            {isPrivate && <div className={styles.createProjectModal__members} ref={membersRef}>
                                <div className={styles.createProjectModal__members_header}>
                                    <div className={styles.createProjectModal__sub}>
                                        Поделиться с:
                                    </div>
                                    <div className={styles.createProjectModal__members_headerRight}>
                                        {selectedMembers.length > 0 && (
                                            <div className={styles.createProjectModal__members_avatarStack}>
                                                {selectedMembers.slice(0, 4).map(member => (
                                                    member.avatar ? (
                                                        <img
                                                            key={member.id}
                                                            className={styles.createProjectModal__members_headerAvatar}
                                                            src={member.avatar}
                                                            alt={getMemberName(member)}
                                                            onClick={() => toggleMember(member.id)}
                                                        />
                                                    ) : (
                                                        <div
                                                            key={member.id}
                                                            className={styles.createProjectModal__members_headerAvatarPlaceholder}
                                                            onClick={() => toggleMember(member.id)}
                                                        >
                                                            {getMemberInitials(member)}
                                                        </div>
                                                    )
                                                ))}
                                                {selectedMembers.length > 4 && (
                                                    <div className={styles.createProjectModal__members_headerAvatarPlaceholder}>
                                                        +{selectedMembers.length - 4}
                                                    </div>
                                                )}
                                            </div>
                                        )}
                                        <div
                                            className={styles.createProjectModal__members_addBtn}
                                            onClick={() => {
                                                setIsColorPickerOpen(false)
                                                setMembersOpen(!membersOpen)
                                                if (!membersOpen) {
                                                    setSearch('')
                                                }
                                            }}
                                        >
                                            <svg width="32" height="32" viewBox="0 0 32 32" fill="none"
                                                xmlns="http://www.w3.org/2000/svg">
                                                <rect x="1" y="1" width="30" height="30" rx="15" fill="#F6F5F9" />
                                                <rect x="1" y="1" width="30" height="30" rx="15" stroke="white"
                                                    strokeWidth="2" />
                                                <path fillRule="evenodd" clipRule="evenodd"
                                                    d="M16 9C16.5523 9 17 9.44772 17 10V22C17 22.5523 16.5523 23 16 23C15.4477 23 15 22.5523 15 22V10C15 9.44772 15.4477 9 16 9Z"
                                                    fill="#B1AEC7" />
                                                <path fillRule="evenodd" clipRule="evenodd"
                                                    d="M9 16C9 15.4477 9.44772 15 10 15L22 15C22.5523 15 23 15.4477 23 16C23 16.5523 22.5523 17 22 17H10C9.44772 17 9 16.5523 9 16Z"
                                                    fill="#B1AEC7" />
                                            </svg>
                                        </div>
                                    </div>
                                </div>

                                {membersOpen && (
                                    <div className={styles.createProjectModal__members_searchWrap}>
                                        <div className={`${styles.createProjectModal__members_searchInput} ${search ? styles.createProjectModal__members_searchInputFocused : ''}`}>
                                            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                                <path fillRule="evenodd" clipRule="evenodd" d="M4.99902 11.2922C4.99902 7.8171 7.81612 5 11.2912 5C14.7662 5 17.5833 7.8171 17.5833 11.2922C17.5833 12.7779 17.0685 14.1431 16.2075 15.2196L16.1635 15.2745L18.8148 17.9259C19.0605 18.1716 19.0605 18.57 18.8148 18.8157C18.5691 19.0614 18.1707 19.0614 17.925 18.8157L15.2736 16.1644L15.2187 16.2084C14.1422 17.0695 12.7769 17.5843 11.2912 17.5843C7.81612 17.5843 4.99902 14.7672 4.99902 11.2922ZM11.2912 6.25843C8.51113 6.25843 6.25746 8.51211 6.25746 11.2922C6.25746 14.0722 8.51113 16.3259 11.2912 16.3259C14.0712 16.3259 16.3249 14.0722 16.3249 11.2922C16.3249 8.51211 14.0712 6.25843 11.2912 6.25843Z" fill="currentColor" />
                                            </svg>
                                            <input
                                                placeholder="Поиск участников..."
                                                value={search}
                                                onChange={(e) => setSearch(e.target.value)}
                                                autoFocus
                                            />
                                        </div>
                                        <div className={styles.createProjectModal__members_list}>
                                            <div className={styles.createProjectModal__members_listScroll}>
                                                {filteredMembers.length === 0 ? (
                                                    <div className={styles.createProjectModal__members_empty}>
                                                        Участники не найдены
                                                    </div>
                                                ) : filteredMembers.map(member => (
                                                    <div
                                                        key={member.id}
                                                        className={styles.createProjectModal__members_item}
                                                        onClick={() => toggleMember(member.id)}
                                                    >
                                                        <div className={styles.createProjectModal__members_itemInfo}>
                                                            {member.avatar ? (
                                                                <img
                                                                    className={styles.createProjectModal__members_itemAvatar}
                                                                    src={member.avatar}
                                                                    alt={getMemberName(member)}
                                                                />
                                                            ) : (
                                                                <div className={styles.createProjectModal__members_itemAvatarPlaceholder}>
                                                                    {getMemberInitials(member)}
                                                                </div>
                                                            )}
                                                            <span>{getMemberName(member)}</span>
                                                        </div>
                                                        {taskData.initial_members.includes(member.id)
                                                            ? <CheckboxActiveIcon />
                                                            : <CheckboxIcon />
                                                        }
                                                    </div>
                                                ))}
                                            </div>
                                        </div>
                                    </div>
                                )}

                            </div>}
                            <button
                                className={styles.createProjectModal__next}
                                onClick={continueToStatuses}
                                type="button"
                            >
                                Продолжить
                            </button>
                            <div className={styles.createProjectModal__progressLine}>
                                <div className={styles.createProjectModal__progressLine_progress} style={{ width: '50%' }} />
                            </div>
                        </div>
                    </div>
                )}

                {step === 2 && (
                    <div style={{ width: "100%" }}>
                        <div className={styles.createProjectModal__presets}>
                            <div className={styles.createProjectModal__plate_title}>
                                Статусы задач<span className={styles.createProjectModal__required}>*</span>
                            </div>
                            <div className={styles.createProjectModal__presets_select} ref={presetsRef}>
                                <div
                                    className={`${styles.createProjectModal__presets_trigger} ${selectedPreset ? styles.createProjectModal__presets_triggerSelected : ''} ${presetsOpen ? styles.createProjectModal__presets_triggerOpen : ''} ${validationErrors.statusPreset ? styles.createProjectModal__presets_triggerError : ''}`}
                                    onClick={() => setPresetsOpen(!presetsOpen)}
                                    onKeyDown={(event) => {
                                        if (event.key === 'Enter' || event.key === ' ') {
                                            event.preventDefault()
                                            setPresetsOpen(!presetsOpen)
                                        }
                                    }}
                                    role="button"
                                    tabIndex={0}
                                    aria-invalid={Boolean(validationErrors.statusPreset)}
                                    aria-describedby={validationErrors.statusPreset ? 'project-status-preset-error' : undefined}
                                >
                                    <span className={selectedPreset ? styles.createProjectModal__presets_triggerValue : styles.createProjectModal__presets_triggerPlaceholder}>
                                        {selectedPreset ? selectedPreset.title : '\u0412\u044b\u0431\u0435\u0440\u0438\u0442\u0435 \u0448\u0430\u0431\u043b\u043e\u043d \u0441\u0442\u0430\u0442\u0443\u0441\u043e\u0432'}
                                    </span>
                                    <svg
                                        width="20" height="20" viewBox="0 0 20 20" fill="none"
                                        xmlns="http://www.w3.org/2000/svg"
                                        style={{
                                            transform: presetsOpen ? 'rotate(180deg)' : 'rotate(0deg)',
                                            transition: 'transform 0.2s ease',
                                        }}
                                    >
                                        <path d="M5 7.5L10 12.5L15 7.5" stroke="#64617E" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                                    </svg>
                                </div>
                                {presetsOpen && (
                                    <div className={styles.createProjectModal__presets_dropdown}>
                                        {presets.map(preset => (
                                            <div
                                                key={preset.key}
                                                className={`${styles.createProjectModal__presets_item} ${taskData.status_preset_key === preset.key ? styles.createProjectModal__presets_itemActive : ''}`}
                                                onClick={() => selectPreset(preset.key)}
                                            >
                                                <span>{preset.title}</span>
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </div>
                            {validationErrors.statusPreset && (
                                <div id="project-status-preset-error" className={styles.createProjectModal__fieldError} role="alert">
                                    {validationErrors.statusPreset}
                                </div>
                            )}
                        </div>

                        {statuses.length > 0 && <div className={styles.createProjectModal__statusList}>
                            <div className={styles.createProjectModal__statusList_items}>
                            {statuses.map((status, index) => {
                                const isDraggable = canDrag(status, index)
                                const showLock = index === 0 || (status.isFixed && index === lastFixedNonReopenedIndex && index !== 0)
                                const showDragHandle = !showLock

                                return (
                                    <div
                                        key={status.id}
                                        ref={(el) => { if (el) statusRowRefs.current.set(status.id, el); else statusRowRefs.current.delete(status.id) }}
                                        className={`
                                            ${styles.createProjectModal__statusList_item}
                                            ${dragItemId === status.id ? styles.createProjectModal__statusList_itemDragging : ''}
                                            ${dragOverId === status.id ? styles.createProjectModal__statusList_itemDragOver : ''}
                                        `}
                                        draggable={isDraggable}
                                        onDragStart={isDraggable ? (e) => handleDragStart(e, status.id) : undefined}
                                        onDragOver={isDraggable ? (e) => handleDragOver(e, status.id, index) : undefined}
                                        onDragLeave={isDraggable ? handleDragLeave : undefined}
                                        onDrop={isDraggable ? (e) => handleDrop(e, status.id, index) : undefined}
                                        onDragEnd={handleDragEnd}
                                    >
                                        {showDragHandle ? (
                                            <div
                                                className={`
                                                    ${styles.createProjectModal__statusList_dragHandle}
                                                    ${!isDraggable ? styles.createProjectModal__statusList_dragHandleStatic : ''}
                                                `}
                                            >
                                                <DragHandleIcon />
                                            </div>
                                        ) : (
                                            <div className={styles.createProjectModal__statusList_lockIcon}>
                                                <LockIcon />
                                            </div>
                                        )}

                                        <div
                                            className={styles.createProjectModal__statusList_colorDot}
                                            style={{ background: status.color }}
                                        />

                                        {editingId === status.id ? (
                                            <input
                                                ref={editInputRef}
                                                className={styles.createProjectModal__statusList_nameInput}
                                                value={editingName}
                                                onChange={(e) => setEditingName(e.target.value)}
                                                onBlur={finishRename}
                                                onKeyDown={(e) => {
                                                    if (e.key === 'Enter') finishRename()
                                                    if (e.key === 'Escape') {
                                                        setEditingId(null)
                                                        setEditingName('')
                                                    }
                                                }}
                                            />
                                        ) : (
                                            <div className={styles.createProjectModal__statusList_name}>
                                                {status.name}
                                            </div>
                                        )}

                                        {hasMenu(status) ? (
                                            <DropDown
                                                isOpen={openMenuId === status.id}
                                                setIsOpen={(nextIsOpen) => {
                                                    setOpenMenuId(nextIsOpen ? status.id : null)
                                                    setColorPickerId(null)
                                                }}
                                                position="right"
                                                dropdownClassName={styles.createProjectModal__statusList_portalDropdown}
                                                contentClassName={styles.createProjectModal__statusList_dropdownContent}
                                                btn={
                                                    <div className={styles.createProjectModal__statusList_dots}>
                                                        <DotsVerticalIcon />
                                                    </div>
                                                }
                                            >
                                                <div className={styles.createProjectModal__statusList_dropdown}>
                                                    {status.id === REOPENED_KEY ? (
                                                        <div
                                                            className={`${styles.createProjectModal__statusList_dropdownItem} ${styles.createProjectModal__statusList_dropdownItemDanger}`}
                                                            onClick={(e) => {
                                                                e.stopPropagation()
                                                                removeReopened()
                                                            }}
                                                        >
                                                            <span className={styles.createProjectModal__statusList_dropdownIconBox}>
                                                                <TrashStatusIcon />
                                                            </span>
                                                            <span>Удалить</span>
                                                        </div>
                                                    ) : (
                                                        <>
                                                            <div
                                                                className={styles.createProjectModal__statusList_dropdownItem}
                                                                onClick={(e) => {
                                                                    e.stopPropagation()
                                                                    startRename(status)
                                                                }}
                                                            >
                                                                <span className={styles.createProjectModal__statusList_dropdownIconBox}>
                                                                    <RenameStatusIcon />
                                                                </span>
                                                                <span>Переименовать</span>
                                                            </div>
                                                            <div
                                                                className={styles.createProjectModal__statusList_dropdownItem}
                                                                onClick={(e) => {
                                                                    e.stopPropagation()
                                                                    openStatusColorPicker(status.id)
                                                                }}
                                                            >
                                                                <span className={styles.createProjectModal__statusList_dropdownIconBox}>
                                                                    <PaletteIcon />
                                                                </span>
                                                                <span>Изменить цвет</span>
                                                            </div>
                                                            <div
                                                                className={`${styles.createProjectModal__statusList_dropdownItem} ${styles.createProjectModal__statusList_dropdownItemDanger}`}
                                                                onClick={(e) => {
                                                                    e.stopPropagation()
                                                                    deleteStatus(status.id)
                                                                }}
                                                            >
                                                                <span className={styles.createProjectModal__statusList_dropdownIconBox}>
                                                                    <TrashStatusIcon />
                                                                </span>
                                                                <span>Удалить</span>
                                                            </div>
                                                        </>
                                                    )}
                                                </div>
                                            </DropDown>
                                        ) : null}
                                    </div>
                                )
                            })}
                            </div>

                            <div
                                className={`
                                    ${styles.createProjectModal__addStatus}
                                    ${statuses.length >= MAX_STATUSES ? styles.createProjectModal__addStatusDisabled : ''}
                                `}
                                onClick={addStatus}
                            >
                                <span className={styles.createProjectModal__addStatusIcon}>
                                    <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                                        <path fillRule="evenodd" clipRule="evenodd"
                                            d="M8 1C8.36819 1 8.66667 1.29848 8.66667 1.66667V14.3333C8.66667 14.7015 8.36819 15 8 15C7.63181 15 7.33333 14.7015 7.33333 14.3333V1.66667C7.33333 1.29848 7.63181 1 8 1Z"
                                            fill="currentColor" />
                                        <path fillRule="evenodd" clipRule="evenodd"
                                            d="M1 8C1 7.63181 1.29848 7.33333 1.66667 7.33333H14.3333C14.7015 7.33333 15 7.63181 15 8C15 8.36819 14.7015 8.66667 14.3333 8.66667H1.66667C1.29848 8.66667 1 8.36819 1 8Z"
                                            fill="currentColor" />
                                    </svg>
                                </span>
                                Добавить статус
                            </div>

                        </div>}

                        <button
                            className={styles.createProjectModal__next}
                            onClick={createProjectHandler}
                            type="button"
                        >
                            Создать проект
                        </button>
                        <div className={styles.createProjectModal__progressLine}>
                            <div className={styles.createProjectModal__progressLine_progress} style={{ width: '100%' }} />
                        </div>
                    </div>
                )}
            </div>
        </Modal>
        {colorPickerId && colorPickerPortalPos && typeof document !== 'undefined' && createPortal(
            <div
                ref={colorPickerPortalRef}
                className={styles.createProjectModal__colorPickerPortal}
                style={{ top: colorPickerPortalPos.top, left: colorPickerPortalPos.left }}
            >
                <div className={styles.createProjectModal__statusList_colorPickerTitle}>Выбор цвета</div>
                <div className={styles.createProjectModal__statusList_colorPickerGrid}>
                    {STATUS_COLORS.map(color => (
                        <ColorItemPicker
                            key={color}
                            currentColor={color}
                            isActive={statuses.find(s => s.id === colorPickerId)?.color === color}
                            getColor={() => changeStatusColor(colorPickerId!, color)}
                        />
                    ))}
                </div>
            </div>,
            document.body
        )}
        </>
    )
}

